/**
 * Rewrites the title of a ReportLab-generated one-page report.
 *
 * The report prints the project's name in the top-left, baked in when the survey ran. Renaming a
 * project therefore leaves the document showing the old name. Rather than regenerating the report —
 * which only the ML pipeline can do — the title is rewritten in the downloaded copy.
 *
 * The rewrite is an *incremental update*: the amended content stream and a one-entry cross-reference
 * table are appended, chained to the original via `/Prev`. Every existing object is left byte for
 * byte intact, so the multi-megabyte map images are never re-encoded, and the change is to the text
 * itself rather than something painted over it — extraction and copy-paste stay correct.
 *
 * Ported from `scripts/renamePdfTitle.ts`, which runs the same algorithm under Node.
 */

import { inflate } from 'pako';

/**
 * PDF byte offsets are what make an incremental update valid, so the file is handled as latin1
 * where one character is exactly one byte. `TextDecoder('latin1')` cannot be used: every latin1
 * label in the WHATWG encoding standard resolves to windows-1252, which remaps 0x80-0x9F and would
 * silently corrupt binary streams.
 */
const bytesToLatin1 = (bytes: Uint8Array): string => {
  const CHUNK = 0x8000; // keep the argument list well inside the engine's apply() limit
  let text = '';

  for (let offset = 0; offset < bytes.length; offset += CHUNK) {
    text += String.fromCharCode(...bytes.subarray(offset, offset + CHUNK));
  }

  return text;
};

const latin1ToBytes = (text: string): Uint8Array => {
  const bytes = new Uint8Array(text.length);

  for (let index = 0; index < text.length; index += 1) {
    bytes[index] = text.charCodeAt(index) & 0xff;
  }

  return bytes;
};

/** ReportLab writes content streams as ASCII85 then Flate, and JavaScript has no ASCII85 decoder. */
const decodeAscii85 = (input: string): Uint8Array => {
  let data = input.replace(/\s/g, '');
  if (data.startsWith('<~')) data = data.slice(2);

  const end = data.indexOf('~>');
  if (end !== -1) data = data.slice(0, end);

  const out: number[] = [];
  let group: number[] = [];

  const flush = (count: number) => {
    while (group.length < 5) group.push(84); // pad with 'u'

    let value = 0;
    for (const digit of group) value = value * 85 + digit;

    const bytes = [value >>> 24, (value >>> 16) & 0xff, (value >>> 8) & 0xff, value & 0xff];
    out.push(...bytes.slice(0, count));
    group = [];
  };

  for (const char of data) {
    if (char === 'z' && group.length === 0) {
      out.push(0, 0, 0, 0);
      continue;
    }

    group.push(char.charCodeAt(0) - 33);
    if (group.length === 5) flush(4);
  }

  if (group.length > 0) flush(group.length - 1);

  return Uint8Array.from(out);
};

const applyFilters = (raw: string, filters: string[]): string =>
  filters.reduce((data, filter) => {
    if (filter === 'ASCII85Decode') return bytesToLatin1(decodeAscii85(data));
    if (filter === 'FlateDecode') return bytesToLatin1(inflate(latin1ToBytes(data)));
    throw new Error(`Unsupported stream filter: /${filter}`);
  }, raw);

interface ContentStream {
  objectNumber: number;
  generation: number;
  text: string;
}

/** The page content stream: the first stream whose decoded body shows text. */
const findContentStream = (pdf: string): ContentStream => {
  const objectHeader = /(\d+)\s+(\d+)\s+obj\b/g;
  let match: RegExpExecArray | null;

  while ((match = objectHeader.exec(pdf)) !== null) {
    const streamStart = pdf.indexOf('stream', match.index);
    const objectEnd = pdf.indexOf('endobj', match.index);
    if (streamStart === -1 || (objectEnd !== -1 && objectEnd < streamStart)) continue;

    const dictionary = pdf.slice(match.index, streamStart);
    if (/\/Subtype\s*\/Image/.test(dictionary)) continue;

    const filters = [...dictionary.matchAll(/\/(ASCII85Decode|FlateDecode|[A-Za-z0-9]+Decode)/g)].map(
      (filter) => filter[1],
    );

    let bodyStart = streamStart + 'stream'.length;
    while (pdf.charCodeAt(bodyStart) === 0x0d || pdf.charCodeAt(bodyStart) === 0x0a) bodyStart += 1;
    const bodyEnd = pdf.indexOf('endstream', bodyStart);

    try {
      const text = applyFilters(pdf.slice(bodyStart, bodyEnd), filters);
      if (/\)\s*Tj/.test(text)) {
        return { objectNumber: Number(match[1]), generation: Number(match[2]), text };
      }
    } catch {
      // Not a decodable content stream — keep looking.
    }
  }

  throw new Error('No page content stream containing text was found');
};

const escapePdfString = (value: string): string => value.replace(/([\\()])/g, '\\$1');

/** `Some-Project Name` and `some project name` are the same title for matching purposes. */
const normaliseTitle = (value: string): string =>
  value
    .replace(/[-_\s]+/g, ' ')
    .trim()
    .toLowerCase();

/**
 * Replaces the title text operator.
 *
 * Prefers the operator whose text matches `expectedTitle`, because "the first `(...) Tj`" is only
 * the title for as long as the report draws nothing else before it. The first operator remains the
 * fallback for when the old name is unknown or has itself been edited.
 */
const replaceTitle = (content: string, newTitle: string, expectedTitle?: string) => {
  const operators = [...content.matchAll(/\(((?:\\.|[^\\()])+)\)(\s*)Tj/g)];
  if (!operators.length) throw new Error('No title text operator found in the content stream');

  const match =
    (expectedTitle &&
      operators.find((operator) => normaliseTitle(operator[1]) === normaliseTitle(expectedTitle))) ||
    operators[0];

  const start = match.index ?? 0;

  return {
    previous: match[1],
    text:
      content.slice(0, start) +
      `(${escapePdfString(newTitle)})${match[2]}Tj` +
      content.slice(start + match[0].length),
  };
};

const trailerValue = (pdf: string, key: string): string => {
  const trailer = pdf.slice(pdf.lastIndexOf('trailer'));
  const match = new RegExp(`/${key}\\s+([^/\\n\\r]+)`).exec(trailer);
  if (!match) throw new Error(`Trailer is missing /${key}`);

  return match[1].trim();
};

/**
 * Appends the rewritten stream plus a one-entry xref section chained to the original via `/Prev`.
 * The stream is written uncompressed, which keeps the appended bytes readable and avoids having to
 * implement an ASCII85 encoder.
 */
const buildIncrementalUpdate = (pdf: string, stream: ContentStream, newContent: string): string => {
  const startxref = /startxref\s+(\d+)/.exec(pdf.slice(-200));
  if (!startxref) throw new Error('No startxref found; the file is not a linear PDF we can append to');

  const previousStartxref = Number(startxref[1]);

  const object =
    `\n${stream.objectNumber} ${stream.generation} obj\n<< /Length ${newContent.length} >>\nstream\n` +
    newContent +
    '\nendstream\nendobj\n';

  const objectOffset = pdf.length + 1; // past the leading newline
  const xrefOffset = pdf.length + object.length;
  const offsetEntry = `${String(objectOffset).padStart(10, '0')} ${String(stream.generation).padStart(5, '0')} n \n`;

  const xref =
    `xref\n${stream.objectNumber} 1\n${offsetEntry}` +
    'trailer\n<<\n' +
    `/Size ${Math.max(Number(trailerValue(pdf, 'Size')), stream.objectNumber + 1)}\n` +
    `/Root ${trailerValue(pdf, 'Root')}\n` +
    `/Info ${trailerValue(pdf, 'Info')}\n` +
    `/Prev ${previousStartxref}\n` +
    `>>\nstartxref\n${xrefOffset}\n%%EOF\n`;

  return pdf + object + xref;
};

/**
 * Returns the report with its title replaced, or the original bytes unchanged if it cannot be
 * rewritten — a report showing a stale name is a far better outcome than a failed download, so
 * callers get something valid either way and the reason is reported through `onError`.
 */
export const renamePdfTitle = (
  pdf: Uint8Array,
  newTitle: string,
  options: { expectedTitle?: string; onError?: (error: unknown) => void } = {},
): Uint8Array => {
  try {
    const text = bytesToLatin1(pdf);
    const stream = findContentStream(text);
    const { text: rewritten } = replaceTitle(stream.text, newTitle, options.expectedTitle);

    return latin1ToBytes(buildIncrementalUpdate(text, stream, rewritten));
  } catch (error) {
    options.onError?.(error);
    return pdf;
  }
};
